import * as React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert as UiAlert } from "components/ui/alert";
import { Button } from "components/ui/button";
import "./Alert.css";
import { apiErrorStr } from "../../helpers";

interface AlertInfo {
  id?: number;
  title?: string;
  body?: string;
  severity?: "error" | "warning" | "info" | "success";
  dismissAfterMs?: number;
}

export interface MyAlertsRef {
  addApiErrorAlert: (e: any) => void;
  addApiSuccessAlert: (body: string, opts?: Partial<AlertInfo>) => void;
  addAlert: (alert: AlertInfo) => void;
}

const alertSeverityClasses: Record<NonNullable<AlertInfo["severity"]>, string> = {
  error: "border-destructive/30 bg-destructive/10",
  warning: "border-amber-300 bg-amber-50 text-amber-950",
  info: "border-blue-200 bg-blue-50 text-blue-950",
  success: "border-emerald-300 bg-emerald-50 text-emerald-950",
};

const MyAlerts = React.forwardRef<MyAlertsRef>((_, ref) => {
  const [alerts, setAlerts] = React.useState<Array<AlertInfo>>([]);
  const nextIdRef = React.useRef(1);

  const dismissAlert = React.useCallback((id: number) => {
    setAlerts((prevAlerts) => prevAlerts.filter((a) => a.id !== id));
  }, []);

  const addAlert = React.useCallback(
    (alert: AlertInfo) => {
      const id = nextIdRef.current++;
      const newAlert = { ...alert, id };
      setAlerts((prevAlerts) => [...prevAlerts, newAlert]);

      if (alert.dismissAfterMs) {
        setTimeout(() => {
          dismissAlert(id);
        }, alert.dismissAfterMs);
      }
    },
    [dismissAlert],
  );

  // convenience
  const addApiErrorAlert = React.useCallback(
    (e: any) => {
      addAlert({
        title: "Error",
        severity: "error",
        body: apiErrorStr(e),
      });
    },
    [addAlert],
  );

  const addApiSuccessAlert = React.useCallback(
    (body: string, opts?: Partial<AlertInfo>) => {
      addAlert({
        title: "Success!",
        severity: "success",
        dismissAfterMs: 5000,
        body,
        ...opts,
      });
    },
    [addAlert],
  );

  React.useImperativeHandle(
    ref,
    () => ({
      addApiErrorAlert,
      addApiSuccessAlert,
      addAlert,
    }),
    [addApiErrorAlert, addApiSuccessAlert, addAlert],
  );

  return (
    <div className="sticky-alert">
      <div className="absolute right-0 left-0">
        {alerts.map((alert) => (
          <UiAlert
            key={`alert_${alert.id}`}
            className={`mb-2 ${alertSeverityClasses[alert.severity || "info"]}`}
            variant={alert.severity === "error" ? "destructive" : "default"}
          >
            <div className="col-span-2 flex items-start justify-between gap-3">
              <div className="min-w-0">
                {alert.title ? (
                  <p className="font-medium">{alert.title}</p>
                ) : null}
                {alert.body ? <p className="mt-1">{alert.body}</p> : null}
              </div>
              {alert.id !== undefined ? (
                <Button
                  aria-label="Dismiss alert"
                  className="-mr-2 -mt-1 shrink-0"
                  onClick={() => dismissAlert(alert.id as number)}
                  size="icon-xs"
                  type="button"
                  variant="ghost"
                >
                  <FontAwesomeIcon aria-hidden="true" icon="xmark" />
                </Button>
              ) : null}
            </div>
          </UiAlert>
        ))}
      </div>
    </div>
  );
});

MyAlerts.displayName = "MyAlerts";

export default MyAlerts;
